import { useMutation, useQueryClient } from "react-query";
import { cancelFutureDeactivation } from "api/customers";
import { useGlobalToast } from "components/GlobalToastProvider";

export interface UseCancelFutureDeactivationOptions {
  onSuccess?: (data: any) => void;
  onError?: (error: unknown) => void;
  onSettled?: () => void;
}

/**
 * Custom hook for canceling a scheduled future deactivation for a client.
 * Handles calling the cancel-future-deactivation endpoint with proper error handling.
 *
 * @example
 * const { cancelDeactivation, isLoadingCancelDeactivation } = useCancelFutureDeactivation({
 *   onSuccess: (data) => {
 *     showToast({ message: "Deactivation cancelled!" });
 *   }
 * });
 *
 * cancelDeactivation(clientId);
 */
export const useCancelFutureDeactivation = (
  options: UseCancelFutureDeactivationOptions = {}
): {
  cancelDeactivation: (clientId: string) => void;
  isLoadingCancelDeactivation: boolean;
  isCancelDeactivationError: boolean;
  isCancelDeactivationSuccess: boolean;
  [key: string]: any;
} => {
  const { onSuccess, onError, onSettled } = options;
  const { showToast } = useGlobalToast();
  const queryClient = useQueryClient();

  const { mutate, isLoading, isError, isSuccess, ...rest } = useMutation(
    (clientId: string) => cancelFutureDeactivation(clientId),
    {
      onSuccess: (data) => {
        // Invalidate all queries to force refresh of client data and deactivation status
        queryClient.invalidateQueries();

        onSuccess?.(data);
      },
      onError: (error: unknown) => {
        console.error("Error cancelling future deactivation:", error);
        showToast({
          message: "Whoops! Failed to cancel future deactivation",
          errorState: true,
        });
        onError?.(error);
      },
      onSettled: () => {
        onSettled?.();
      },
    }
  );

  return {
    cancelDeactivation: mutate,
    isLoadingCancelDeactivation: isLoading,
    isCancelDeactivationError: isError,
    isCancelDeactivationSuccess: isSuccess,
    ...rest,
  };
};

export default useCancelFutureDeactivation;
